import { observer } from 'mobx-react-lite';
import { automaticColorString } from './ColorPicker.locstring.json';
import loc from 'owa-localize';
import * as React from 'react';
import { ColorPicker as ColorHexPicker } from '@fluentui/react/lib/ColorPicker';
import type { ColorScheme } from 'owa-color-utils/lib/ColorScheme';
import { ControlIcons } from 'owa-control-icons';
import { DefaultButton } from '@fluentui/react/lib/Button';
import { getFirstFocusable, KeyCodes } from '@fluentui/react/lib/Utilities';
import { Icon } from '@fluentui/react/lib/Icon';
import { Modal } from '@fluentui/react/lib/Modal';
import {
    IColorCellProps,
    IColorPickerGridCellStyleProps,
    IColorPickerGridCellStyles,
    ISwatchColorPickerStyles,
    ISwatchColorPickerStyleProps,
    SwatchColorPicker,
} from '@fluentui/react/lib/SwatchColorPicker';
import type { IColor } from '@fluentui/react/lib/Color';

let styles = require('./ColorPicker.scss');
let classNames = require('classnames/bind').bind(styles);
let defaultColor = '#4aa02c';

export interface ColorPickerProps {
    selectCalendarColor: (colorScheme: ColorScheme) => void;
    selectHexCalendarColor: (calendarHexColor: string) => void;
    selectedScheme: ColorScheme;
    availableColors: ColorScheme[];
}

export interface ColorPickerState {
    showColorPicker: boolean;
}

export default observer(function ColorPicker(props: ColorPickerProps) {
    const swatchRef = React.useRef<HTMLElement>();
    const selectedHexColor = React.useRef<string>();
    const [showColorPicker, setShowColorPicker] = React.useState<boolean>(false);
    const onModalDismiss = () => {
        setShowColorPicker(false);
    };
    const generateColorCells = (): IColorCellProps[] => {
        let returnValue: IColorCellProps[] = [];
        props.availableColors.forEach(scheme => {
            let colorCell: IColorCellProps = {
                id: 'colorCell' + scheme.calPrimary.substring(1),
                color: scheme.calPrimary,
                label: typeof scheme.colorName === 'string' ? scheme.colorName : scheme.colorName(),
            };
            returnValue.push(colorCell);
        });
        return returnValue;
    };
    /**
     * using currying to handle allowing button's onClick to pass in the scheme parameter instead of just the MouseEvent,
     * while still only generating the callback once instead of on every render
     * https://stackoverflow.com/a/45448802
     */
    const onAutomaticButtonClick = e => {
        setColor(null);
    };
    const onHexColorChanged = (ev: React.SyntheticEvent<HTMLElement>, color: IColor) => {
        selectedHexColor.current = color.hex;
    };
    const handleColorButtonClick = e => {
        let color = selectedHexColor.current
            ? selectedHexColor.current
            : props.selectedScheme.calPrimary;
        props.selectHexCalendarColor(color);
        setShowColorPicker(false);
    };
    const onKeyDown = (evt: React.KeyboardEvent<unknown>) => {
        if (evt.which == KeyCodes.down || evt.which == KeyCodes.tab) {
            evt.preventDefault();
            evt.stopPropagation();
            let node = swatchRef.current;
            let firstFocusable = getFirstFocusable(
                node,
                node,
                true /* includeElementsInFocusZones */
            );
            firstFocusable.focus();
        }
    };
    const setColor = (id: string, color?: string) => {
        let selectedScheme;
        let selectedSchemeArray = props.availableColors.filter(e => e.calPrimary == color);
        if (selectedSchemeArray.length > 0) {
            selectedScheme = selectedSchemeArray[0];
        }
        props.selectCalendarColor(selectedScheme);
    };
    let selectedId = props.selectedScheme?.calPrimary
        ? 'colorCell' + props.selectedScheme.calPrimary.substring(1)
        : '';
    return (
        <div className={styles.wrapper}>
            <DefaultButton
                key={'Automatic'}
                className={classNames(styles.colorItemStyle, styles.automaticOptionStyle)}
                onClick={onAutomaticButtonClick}
                onKeyDown={onKeyDown}>
                <Icon
                    iconName={!props.selectedScheme && ControlIcons.CheckMark}
                    className={styles.checkmark}
                />
                <span className={styles.automaticOptionSpan}>{loc(automaticColorString)}</span>
            </DefaultButton>
            <div ref={ref => (swatchRef.current = ref)}>
                <SwatchColorPicker
                    colorCells={generateColorCells()}
                    columnCount={5}
                    cellShape={'circle'}
                    selectedId={selectedId}
                    onColorChanged={setColor}
                    className={styles.swatchColorPicker}
                    shouldFocusCircularNavigate={false}
                    getColorGridCellStyles={getColorGridCellStyles}
                    styles={styles_0}
                />
            </div>
            <Modal isOpen={showColorPicker} onDismiss={onModalDismiss} isBlocking={false}>
                <ColorHexPicker
                    color={props.selectedScheme ? props.selectedScheme.calPrimary : defaultColor}
                    onChange={onHexColorChanged}
                    alphaSliderHidden={true}
                />
                <DefaultButton onClick={handleColorButtonClick}>
                    <span>Confirm</span>
                </DefaultButton>
            </Modal>
        </div>
    );
});

function styles_0(props: ISwatchColorPickerStyleProps): Partial<ISwatchColorPickerStyles> {
    return {
        root: [
            {
                width: 200,
                height: 80,
                padding: '7px 8px 6px 6px',
                borderCollapse: 'collapse',
                margin: '0px',
            },
        ],
        tableCell: [
            {
                padding: '0px',
            },
        ],
    };
}

function getColorGridCellStyles(
    props: IColorPickerGridCellStyleProps
): Partial<IColorPickerGridCellStyles> {
    let { theme, selected } = props;
    return {
        colorCell: [
            {
                padding: 0,
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                overflow: 'visible',
                width: 40,
                height: 40,
                selectors: {
                    $svg: {
                        display: 'flex',
                        justifyContent: 'center',
                        alignItems: 'center',
                        border: 'none',
                        width: 32,
                        height: 32,
                        padding: '0 0 0 1px',
                        boxShadow: 'none',
                    },
                    '.ms-Fabric.is-focusVisible &:focus': {
                        borderRadius: '100%',
                    },
                    '&:hover $svg, &:active $svg, &:focus $svg': {
                        padding: 2,
                        border: '4px solid',
                        width: 24,
                        height: 24,
                        minWidth: 24,
                    },
                    '&:hover $svg, &:focus $svg': {
                        borderColor: theme.palette.neutralQuaternaryAlt,
                        width: 24,
                        height: 24,
                    },
                    '&:active $svg': {
                        borderColor: theme.palette.themeLight + '!important',
                    },
                    '&:focus $svg': {
                        boxShadow: 'none',
                        selectors: {
                            '&:focus::after': {
                                width: 34,
                                height: 34,
                                marginTop: 2,
                                marginLeft: 2,
                            },
                        },
                    },
                    '.ms-Fabric.is-focusVisible &:focus $svg': {
                        boxShadow: 'none',
                        padding: 2,
                        border: '4px solid',
                        borderColor: theme.palette.neutralQuaternaryAlt,
                        width: 24,
                        height: 24,
                        minWidth: 24,
                        selectors: {
                            '&:focus::after': {
                                width: 34,
                                height: 34,
                                marginTop: 2,
                                marginLeft: 2,
                                outline: 'transparent!important',
                                boxShadow: '0 0 0 1px ' + theme.palette.neutralSecondary,
                                border: 'none',
                            },
                        },
                    },
                    '&:focus::after': {
                        borderRadius: '100%',
                    },
                    '&:hover $svg, &:active $svg': {
                        boxShadow: 'none',
                    },
                    '&:hover, &:active, &:focus': {
                        padding: '0!important',
                    },
                    '&:active': {
                        // force the active state to change the border color
                        borderColor: theme.palette.neutralTertiary + '!important',
                    },
                },
            },
            selected &&
                'isSelected' && {
                    selectors: {
                        $svg: {
                            border: '4px solid',
                            borderColor: theme.palette.themeLight + '!important',
                            borderRadius: '100%',
                            padding: 2,
                            boxShadow: 'none',
                        },
                        '.ms-Fabric.is-focusVisible &:focus $svg': {
                            border: '4px solid',
                            borderColor: theme.palette.themeLight + '!important',
                        },
                        '.ms-Fabric.is-focusVisible &:focus $svg, & $svg': {
                            borderRadius: '100%',
                            width: 24,
                            height: 24,
                            minWidth: 24,
                        },
                        '&:hover::before': {
                            width: 40,
                            height: 40,
                        },
                    },
                },
        ],
        svg: [
            {
                width: 32,
                height: 32,
                padding: 2,
                boxSizing: 'content-box',
                boxShadow: 'none',
            },
        ],
    };
}
